// CrossTabSummaryCard — navigation-affordance card on the contract
// Details summary strip. Clicking the card navigates to a different
// tab with a filter pre-applied. Visually mirrors MetricCard (same
// padding, same value typography) but uses navigation a11y semantics
// (no aria-pressed; aria-label describes both count and destination).
// Kept separate from MetricCard so the existing filter-toggle a11y
// model on the deliverables / KPIs / meeting tabs stays honest.

import type { ReactNode } from "react";
import type { MetricCardTone } from "../tracking/deliverable/MetricCard";
import { Link } from "react-router";

interface CrossTabSummaryCardProps {
  label: string;
  value: ReactNode;
  valueTone?: MetricCardTone;
  subtitle?: ReactNode;
  ariaLabel: string;
  to: string;
  testId: string;
}

const VALUE_TONE_CLASS: Record<MetricCardTone, string> = {
  danger: "text-red-600 dark:text-red-400",
  warning: "text-amber-600 dark:text-amber-400",
  neutral: "text-slate-700 dark:text-slate-300",
  success: "text-emerald-600 dark:text-emerald-400",
  brand: "text-brand-600 dark:text-brand-400",
  brandStrong: "text-brand-700 dark:text-brand-400",
};

export function CrossTabSummaryCard({
  label,
  value,
  valueTone = "neutral",
  subtitle,
  ariaLabel,
  to,
  testId,
}: CrossTabSummaryCardProps) {
  return (
    <Link
      to={to}
      data-testid={testId}
      aria-label={ariaLabel}
      className="group block rounded-md p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 transition-colors hover:border-brand-400 dark:hover:border-brand-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
    >
      <div className="flex items-center justify-between">
        <div className="text-caption font-medium text-slate-500 dark:text-slate-400">
          {label}
        </div>
        <span
          data-testid={`${testId}-cue`}
          className="text-caption text-slate-400 dark:text-slate-500 group-hover:text-brand-600 dark:group-hover:text-brand-400"
        >
          View →
        </span>
      </div>
      <div
        data-testid={`${testId}-value`}
        className={`mt-2 text-h2 font-semibold ${VALUE_TONE_CLASS[valueTone]}`}
      >
        {value}
      </div>
      {subtitle && (
        <div
          data-testid={`${testId}-subtitle`}
          className="mt-1 text-caption text-slate-500 dark:text-slate-400 line-clamp-1"
        >
          {subtitle}
        </div>
      )}
    </Link>
  );
}

export default CrossTabSummaryCard;
